import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { UserCircle, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const nameParts = (user?.legal_name || user?.name || "").trim().split(/\s+/).filter(Boolean);
  const [form, setForm] = useState({
    first_name: user?.first_name || nameParts[0] || "",
    middle_name: user?.middle_name || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""),
    last_name: user?.last_name || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""),
    phone: user?.phone || "",
    address_street: user?.address_street || "",
    address_city: user?.address_city || "",
    address_zip: user?.address_zip || "",
    preferred_contact: user?.preferred_contact || "email",
  });
  const [saving, setSaving] = useState(false);
  const [zipInfo, setZipInfo] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupZip = async (z) => {
    if (!/^\d{5}$/.test(z)) { setZipInfo(null); return; }
    try {
      const { data } = await api.get(`/geo/zip/${z}`);
      if (data.valid) {
        setZipInfo(data);
        setForm((f) => ({ ...f, address_city: data.city }));
      } else { setZipInfo(null); }
    } catch { setZipInfo(null); }
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Please enter your first and last name"); return; }
    const legal_name = [form.first_name, form.middle_name, form.last_name].map((s) => s.trim()).filter(Boolean).join(" ");
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { ...form, legal_name });
      setUser(data);
      toast.success("Profile saved");
    } catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-rose-50 text-rose-600"><UserCircle className="h-6 w-6" strokeWidth={2.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50" data-testid="profile-title">Your profile</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">Signed in as {user?.email}</p>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="grid gap-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">First name</Label>
              <Input data-testid="profile-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Middle name</Label>
              <Input data-testid="profile-middle-name" value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} className="mt-1.5" placeholder="Optional" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Last name</Label>
              <Input data-testid="profile-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" />
            </div>
            <p className="sm:col-span-6 -mt-1 text-xs text-slate-400 dark:text-slate-500">Your full legal name (First Middle Last) is used on financing applications and California closing paperwork.</p>
            <div className="sm:col-span-6">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Mobile phone</Label>
              <Input data-testid="profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="+1 415 555 1234" />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 border-t border-slate-100 dark:border-slate-800 pt-6">
            <MapPin className="h-4 w-4 text-rose-600" />
            <p className="font-display text-sm font-bold text-slate-900 dark:text-slate-100">Registration address</p>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Your ZIP sets your local California sales-tax rate on quotes and prefills delivery.</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-6">
            <div className="sm:col-span-6"><Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Street</Label><Input data-testid="profile-street" value={form.address_street} onChange={(e) => set("address_street", e.target.value)} className="mt-1.5" /></div>
            <div className="sm:col-span-4"><Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">City</Label><Input data-testid="profile-city" value={form.address_city} onChange={(e) => set("address_city", e.target.value)} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">ZIP</Label><Input data-testid="profile-zip" value={form.address_zip} onChange={(e) => { const z = e.target.value; set("address_zip", z); lookupZip(z); }} className="mt-1.5" placeholder="90001" /></div>
            {zipInfo && (
              <p className="sm:col-span-6 -mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="zip-hint">
                <span className="font-semibold text-slate-700 dark:text-slate-300">{zipInfo.city}, {zipInfo.state}</span>
                {zipInfo.tax_rate != null && <> · est. <b>{zipInfo.tax_rate}%</b> California sales tax</>}
              </p>
            )}
          </div>

          <div className="mt-6">
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Preferred contact method</Label>
            <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
              <SelectTrigger className="mt-1.5" data-testid="profile-contact"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="sms">Text / SMS</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6 flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3 text-xs text-slate-500 dark:text-slate-400">
            <ShieldCheck className="h-4 w-4 shrink-0 text-rose-600" /> We never ask for your full SSN, password, or card details here.
          </div>

          <Button data-testid="profile-save" onClick={save} disabled={saving} className="mt-6 w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">{saving ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
    </div>
  );
}
